import { useCallback } from 'react';

/**
 * Helpers around the `player.mux` API that initShakaPlayerMux attaches to the
 * player instance.
 *
 * Mux initialization itself lives in useShakaPlayer, which hands the SDK a
 * token-scrubbing Proxy of the player (see createMuxScrubbingPlayer) so signed
 * CDN URLs never reach analytics. Do NOT initialize Mux from anywhere else:
 * calling initShakaPlayerMux with the raw player would leak signed tokens in
 * video_source_url / request_url. Load-error reporting (loadErrorHandler) also
 * lives in useShakaPlayer, next to the monitor instance it needs.
 */
export const useMuxAnalytics = (
  playerRef: React.RefObject<any>,
  onMuxDataUpdate?: (data: any) => void,
) => {
  // Update Mux view metadata mid-view (e.g. attach viewer_user_id / custom_1
  // after an analytics-consent grant). Applies to subsequent beacons of the
  // current view; to clear a field, pass "" rather than undefined.
  const updateMuxData = useCallback((data: any) => {
    if ((playerRef.current as any)?.mux?.updateData) {
      try {
        (playerRef.current as any).mux.updateData(data);
        onMuxDataUpdate?.(data);
      } catch (error) {
        console.error('Failed to update Mux data:', error);
      }
    }
  }, [onMuxDataUpdate, playerRef]);

  const destroyMux = useCallback(() => {
    // Clean up Mux monitoring
    if ((playerRef.current as any)?.mux?.destroy) {
      try {
        (playerRef.current as any).mux.destroy();
      } catch (error) {
        console.error('Error destroying Mux:', error);
      }
    }
  }, [playerRef]);

  return {
    updateMuxData,
    destroyMux
  };
};
